import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button, Spin } from 'antd';
import { CloseOutlined, CloudOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getFieldIcon } from '../../components/Forecast/icons';
import { useForecastFields } from '../../hooks/useForecastFields';
import tomorrowIoTimelinesService, { type TimelineInterval } from '../../api/tomorrowIoTimelinesService';

dayjs.extend(utc);
dayjs.extend(timezone);

interface TioForecastPanelProps {
  /** Current theme */
  theme: 'light' | 'dark';
  /** Array of Tomorrow.io timestamps */
  tioTimestamps: string[];
  /** Current index in the timestamps array */
  tioCurrentIndex: number;
  /** Selected event data */
  selectedEvent: {
    demo?: boolean;
    circuit?: {
      latitude?: number;
      longitude?: number;
      time_zone?: string;
    };
  } | null;
  /** Card position for dragging */
  cardPosition: { x: number; y: number } | null;
  /** Whether this card is currently being dragged */
  isDragging: boolean;
  /** Handler for mouse down on the card (for dragging) */
  onMouseDown: (e: React.MouseEvent) => void;
  /** Handler to close the panel */
  onClose: () => void;
  /** Ref to the card element */
  cardRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Tomorrow.io Forecast Panel
 * 
 * Displays real-time forecast data from Tomorrow.io Timelines API.
 * Updates as the timeline position changes in the nowcasting player.
 */
export const TioForecastPanel: React.FC<TioForecastPanelProps> = ({
  theme,
  tioTimestamps,
  tioCurrentIndex,
  selectedEvent,
  cardPosition,
  isDragging,
  onMouseDown,
  onClose,
  cardRef: externalCardRef,
}) => {
  const internalCardRef = useRef<HTMLDivElement>(null);
  const cardRef = externalCardRef || internalCardRef;
  const [timelineData, setTimelineData] = useState<TimelineInterval | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Get forecast fields
  const { hourlyTableFields, loading: isLoadingFields } = useForecastFields();

  // Get API field names from hourly forecast fields (exclude calculated fields)
  const apiFields = useMemo(() => {
    if (hourlyTableFields.length === 0) return [];
    const isDemo = selectedEvent?.demo ?? false;
    return hourlyTableFields
      .filter(field => !field.calculated)
      .filter(field => !field.demo_only || isDemo)
      .map(field => field.api_name);
  }, [hourlyTableFields, selectedEvent?.demo]);

  // Create a stable string key for apiFields to use in dependencies
  const apiFieldsKey = useMemo(() => apiFields.sort().join(','), [apiFields]);

  // Extract circuit coordinates for stable dependencies
  const latitude = selectedEvent?.circuit?.latitude;
  const longitude = selectedEvent?.circuit?.longitude;

  // Local cache for fetched data (avoids refetching the same timestamp within a session)
  const fetchedDataRef = useRef<Map<string, TimelineInterval | null>>(new Map());
  // Generation counter to uniquely identify each effect invocation and prevent
  // a cancelled fetch's .finally() from clobbering a newer fetch's active state
  const fetchGenerationRef = useRef(0);
  // Store apiFields in a ref to avoid dependency issues while always having latest value
  const apiFieldsRef = useRef<string[]>(apiFields);
  useEffect(() => {
    apiFieldsRef.current = apiFields;
  }, [apiFields]);

  // Fetch timeline data when timestamp changes
  useEffect(() => {
    if (tioTimestamps.length === 0 || apiFieldsRef.current.length === 0 || latitude === undefined || longitude === undefined) {
      setTimelineData(null);
      setIsLoading(false);
      fetchedDataRef.current.clear();
      return;
    }

    const currentTimestamp = tioTimestamps[tioCurrentIndex];
    if (!currentTimestamp) return;

    const fetchedDataMap = fetchedDataRef.current;

    // Check cache first
    const cached = tomorrowIoTimelinesService.getCachedTimeline(latitude, longitude, currentTimestamp, apiFieldsRef.current);
    if (cached) {
      setTimelineData(cached);
      setIsLoading(false);
      fetchedDataMap.set(currentTimestamp, cached);
      return;
    }

    // Check local fetch cache
    const alreadyFetched = fetchedDataMap.get(currentTimestamp);
    if (alreadyFetched !== undefined) {
      setTimelineData(alreadyFetched);
      setIsLoading(false);
      return;
    }

    // Each effect invocation gets a unique generation number so that when a
    // cancelled fetch resolves, it cannot clobber a newer fetch's state
    const generation = ++fetchGenerationRef.current;
    let cancelled = false;
    setIsLoading(true);

    tomorrowIoTimelinesService.getTimelineForTimestamp(latitude, longitude, currentTimestamp, apiFieldsRef.current)
      .then((data) => {
        if (cancelled) return;
        fetchedDataMap.set(currentTimestamp, data);
        setTimelineData(data);
        setIsLoading(false);
        if (data === null) {
          fetchedDataMap.delete(currentTimestamp);
        }
      })
      .catch((error) => {
        console.error('Failed to fetch timeline data:', error);
        if (cancelled) return;
        setTimelineData(null);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
      // Bump generation so any future checks also see staleness
      fetchGenerationRef.current = generation + 1;
      fetchedDataMap.delete(currentTimestamp);
    };
  }, [tioTimestamps, tioCurrentIndex, latitude, longitude, apiFieldsKey]);

  // Get current timestamp for display
  const currentTimestamp = tioTimestamps[tioCurrentIndex];
  if (!currentTimestamp) return null;

  const forecastTime = dayjs.utc(currentTimestamp).tz(selectedEvent?.circuit?.time_zone || 'UTC');

  return (
    <div
      ref={cardRef}
      onMouseDown={onMouseDown}
      style={{
        position: 'absolute',
        ...(cardPosition
          ? { left: cardPosition.x, top: cardPosition.y }
          : { bottom: 240, left: 24 }),
        backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
        padding: '12px 14px',
        borderRadius: 10,
        zIndex: isDragging ? 2000 : 1000,
        minWidth: 400,
        maxWidth: 440,
        border: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px)',
        cursor: isDragging ? 'grabbing' : 'default',
      }}
    >
      {/* Header */}
      <div
        className="drag-handle"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: `1px solid ${theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          cursor: 'grab',
          userSelect: 'none',
        }}
      >
        {/* Left side: Panel title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CloudOutlined style={{ fontSize: 14, color: '#1890ff' }} />
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.85)',
            }}
          >
            Tomorrow.io Forecast
          </span>
        </div>
        {/* Right side: Timestamp + close button */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              fontSize: 11,
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.55)',
            }}
          >
            @ {forecastTime.format('HH:mm')}
          </span>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={onClose}
            style={{
              color: theme === 'dark' ? 'rgba(255, 255, 255, 0.65)' : 'rgba(0, 0, 0, 0.65)',
              minWidth: 24,
              padding: '0 4px',
            }}
          />
        </div>
      </div>

      {/* Content */}
      <ForecastContent
        theme={theme}
        timelineData={timelineData}
        isLoading={isLoading}
        isLoadingFields={isLoadingFields}
        hourlyTableFields={hourlyTableFields}
        isDemo={selectedEvent?.demo ?? false}
      />
    </div>
  );
};

// Separate component for the forecast content to keep the main component cleaner
interface ForecastContentProps {
  theme: 'light' | 'dark';
  timelineData: TimelineInterval | null;
  isLoading: boolean;
  isLoadingFields: boolean;
  hourlyTableFields: ReturnType<typeof useForecastFields>['hourlyTableFields'];
  isDemo: boolean;
}

// Type alias for forecast field to avoid referencing visibleFields in callbacks
type ForecastField = ReturnType<typeof useForecastFields>['hourlyTableFields'][0];

const ForecastContent: React.FC<ForecastContentProps> = ({
  theme,
  timelineData,
  isLoading,
  isLoadingFields,
  hourlyTableFields,
  isDemo,
}) => {
  // Get available API field names from the response (memoized)
  // Must be called before early returns (React hooks rule)
  const availableApiNames = useMemo(() => {
    if (!timelineData) return [];
    return Object.keys(timelineData.values);
  }, [timelineData]);

  // Get visible forecast fields, filtered to only those available in the response (memoized)
  const visibleFields = useMemo(() => {
    if (!timelineData) return [];
    return hourlyTableFields
      .filter(field => !field.demo_only || isDemo)
      .filter(field => !field.calculated)
      .filter(field => field.show_in_map_card)
      .filter(field => availableApiNames.includes(field.api_name))
      .sort((a, b) => (a.position ?? 9999) - (b.position ?? 9999));
  }, [hourlyTableFields, isDemo, availableApiNames, timelineData]);

  // Pair fields into rows (2 columns) (memoized)
  const rows = useMemo(() => {
    const result: Array<{ left: ForecastField | null; right: ForecastField | null }> = [];
    for (let i = 0; i < visibleFields.length; i += 2) {
      result.push({
        left: visibleFields[i] || null,
        right: visibleFields[i + 1] || null,
      });
    }
    return result;
  }, [visibleFields]);

  // Helper to get parameter value (memoized)
  const getParameter = useCallback((field: ForecastField): number | null => {
    if (!timelineData) return null;
    const value = timelineData.values[field.api_name];
    return value ?? null;
  }, [timelineData]);

  // Helper to format value with unit (memoized)
  const formatValue = useCallback((field: ForecastField, value: number | null): string | React.ReactNode => {
    if (value === null) return 'N/A';

    // Special handling for wind_direction - show arrow
    if (field.db_name === 'wind_direction') {
      return (
        <span
          style={{
            display: 'inline-block',
            transform: `rotate(${value}deg)`,
            fontSize: 14,
            lineHeight: 1,
          }}
        >
          ↑
        </span>
      );
    }

    // Wind fields: convert m/s to km/h for display (align with Station panel)
    const dbLower = field.db_name.toLowerCase();
    const apiLower = field.api_name?.toLowerCase() ?? '';
    const isWindSpeedOrGust =
      dbLower.includes('wind_speed') ||
      dbLower.includes('wind_gust') ||
      dbLower.includes('windgust') ||
      apiLower === 'windgust' ||
      apiLower === 'windspeed';
    if (isWindSpeedOrGust) {
      const kmhValue = value * 3.6;
      if ((dbLower.includes('gust') || apiLower.includes('gust')) && kmhValue <= 0) return 'N/A';
      return `${kmhValue.toFixed(1)} km/h`;
    }

    // Rain/precipitation: show N/A for zero values
    if ((field.db_name === 'rain_intensity' || field.db_name === 'precipitation_intensity') && value === 0) {
      return 'N/A';
    }

    // Format based on unit type
    const unit = field.unit || '';

    // Determine decimal places based on field type
    let decimals = 1;
    if (unit === '%') decimals = 0;
    if (unit === 'hPa') decimals = 1;

    return `${value.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;
  }, []);

  // Render a single field cell (memoized)
  const renderField = useCallback((field: ForecastField | null, isRight: boolean) => {
    if (!field) {
      return (
        <>
          <td style={{ padding: isRight ? '2px 0' : '2px 6px 2px 0' }}></td>
          <td style={{ padding: isRight ? '2px 0' : '2px 6px 2px 0' }}></td>
        </>
      );
    }

    const value = getParameter(field);

    return (
      <>
        <td style={{ padding: isRight ? '2px 6px 2px 0' : '2px 6px 2px 0', whiteSpace: 'nowrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            {getFieldIcon(field.db_name, { size: 14 })} {field.display_name}
          </span>
        </td>
        <td style={{ padding: isRight ? '2px 0' : '2px 6px 2px 0', textAlign: 'right', fontWeight: 500 }}>
          {formatValue(field, value)}
        </td>
      </>
    );
  }, [getParameter, formatValue]);

  // Show loading state only if we have no data at all (first load)
  if (!timelineData && isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
        <Spin size="small" />
      </div>
    );
  }

  // Show empty state if no data and not loading
  if (!timelineData && !isLoading) {
    return (
      <div
        style={{
          fontSize: 11,
          color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
          textAlign: 'center',
          padding: 8,
        }}
      >
        No forecast data available
      </div>
    );
  }

  if (isLoadingFields) {
    return <Spin size="small" />;
  }

  // Render fields with loading indicator overlay if loading new data
  return (
    <div
      style={{
        fontSize: 11,
        lineHeight: 1.5,
        color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.85)',
        position: 'relative',
      }}
    >
      {/* Loading indicator overlay - only show when loading and we have existing data */}
      {isLoading && timelineData && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 4px',
            backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.8)',
            borderRadius: 4,
            fontSize: 10,
            color: theme === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
          }}
        >
          <Spin size="small" />
          <span>Updating...</span>
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {renderField(row.left, false)}
              {renderField(row.right, true)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TioForecastPanel;
